import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserManagement } from '../context/UserManagementContext';
import { userManagementService, User, BulkActionRequest } from '../services/userManagementService';

export function useUserManagementOperations() {
  const { state, dispatch } = useUserManagement();
  const [searchParams] = useSearchParams();

  // Load users
  const loadUsers = useCallback(async (page: number = 1, limit: number = 10) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await userManagementService.getUsers(page, limit);
      dispatch({ type: 'SET_USERS', payload: { users: response.data, pagination: response.pagination } });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to load users' });
    }
  }, [dispatch]);

  // Load user counts
  const loadUserCounts = useCallback(async () => {
    try {
      const response = await userManagementService.getUserCounts();
      dispatch({ type: 'SET_USER_COUNTS', payload: response.data });
    } catch (error) {
      console.error('Failed to load user counts:', error);
    }
  }, [dispatch]);

  // Search users
  const searchUsers = useCallback(async (query: string, filters: { role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN'; disabled?: boolean }, page: number = 1, limit: number = 10) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await userManagementService.searchUsers({
        query,
        role: filters.role,
        disabled: filters.disabled,
        page,
        limit,
      });
      dispatch({ type: 'SET_USERS', payload: { users: response.data, pagination: response.pagination } });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to search users' });
    }
  }, [dispatch]);

  // Update user
  const updateUser = useCallback(async (id: string, updates: Partial<Pick<User, 'firstName' | 'lastName' | 'avatar'>>) => {
    try {
      const response = await userManagementService.updateUser(id, updates);
      dispatch({ type: 'UPDATE_USER', payload: response.data });
      return { success: true, message: response.message };
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Failed to update user' };
    }
  }, [dispatch]);

  // Update user role
  const updateUserRole = useCallback(async (id: string, role: 'PATIENT' | 'CAREGIVER' | 'ADMIN') => {
    try {
      const response = await userManagementService.updateUserRole(id, role);
      dispatch({ type: 'UPDATE_USER', payload: response.data });
      return { success: true, message: response.message };
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Failed to update user role' };
    }
  }, [dispatch]);

  // Update user status
  const updateUserStatus = useCallback(async (id: string, disabled: boolean) => {
    try {
      const response = await userManagementService.updateUserStatus(id, disabled);
      dispatch({ type: 'UPDATE_USER', payload: response.data });
      return { success: true, message: response.message };
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Failed to update user status' };
    }
  }, [dispatch]);

  // Delete user
  const deleteUser = useCallback(async (id: string) => {
    try {
      await userManagementService.deleteUser(id);
      dispatch({ type: 'REMOVE_USER', payload: id });
      return { success: true, message: 'User deleted successfully' };
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Failed to delete user' };
    }
  }, [dispatch]);

  // Bulk actions
  const performBulkAction = useCallback(async (action: 'delete' | 'disable' | 'enable') => {
    if (state.selectedUsers.length === 0) {
      return { success: false, message: 'No users selected' };
    }

    try {
      const request: BulkActionRequest = {
        userIds: state.selectedUsers,
        action,
      };
      
      const response = await userManagementService.bulkAction(request);
      
      // Update local state based on action
      if (action === 'delete') {
        state.selectedUsers.forEach(userId => {
          dispatch({ type: 'REMOVE_USER', payload: userId });
        });
      } else {
        // For disable/enable, we need to reload users to get updated status
        await loadUsers(state.pagination?.page || 1, state.pagination?.limit || 10);
      }
      
      dispatch({ type: 'CLEAR_SELECTED_USERS' });
      return { success: true, message: response.message };
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Failed to perform bulk action' };
    }
  }, [dispatch, state.selectedUsers, state.pagination, loadUsers]);

  // Load users when component mounts or search query/filters change
  useEffect(() => {
    const hasActiveSearch = state.searchQuery.trim() || state.searchFilters.role || state.searchFilters.disabled !== undefined;
    
    if (hasActiveSearch) {
      // When filters change, reset to page 1
      searchUsers(state.searchQuery, state.searchFilters, 1, state.pagination?.limit || 10);
    } else {
      loadUsers(1, state.pagination?.limit || 10);
    }
  }, [state.searchQuery, state.searchFilters, loadUsers, searchUsers, state.pagination?.limit]);

  // Load user counts when component mounts
  useEffect(() => {
    loadUserCounts();
  }, [loadUserCounts]);

  // Handle URL parameters for initial filtering
  useEffect(() => {
    const role = searchParams.get('role') as 'PATIENT' | 'CAREGIVER' | 'ADMIN' | null;
    const disabled = searchParams.get('disabled');
    
    if (role || disabled !== null) {
      const filters: { role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN'; disabled?: boolean } = {};
      
      if (role) {
        filters.role = role;
      }
      
      if (disabled !== null) {
        filters.disabled = disabled === 'true';
      }
      
      dispatch({ type: 'SET_SEARCH_FILTERS', payload: filters });
    }
  }, [searchParams, dispatch]);

  return {
    // State
    users: state.users,
    userCounts: state.userCounts,
    pagination: state.pagination,
    loading: state.loading,
    error: state.error,
    selectedUsers: state.selectedUsers,
    searchQuery: state.searchQuery,
    searchFilters: state.searchFilters,
    
    // Actions
    loadUsers,
    loadUserCounts,
    searchUsers,
    updateUser,
    updateUserRole,
    updateUserStatus,
    deleteUser,
    performBulkAction,
    
    // Pagination helpers
    goToPage: (page: number) => {
      const hasActiveSearch = state.searchQuery.trim() || state.searchFilters.role || state.searchFilters.disabled !== undefined;
      if (hasActiveSearch) {
        searchUsers(state.searchQuery, state.searchFilters, page, state.pagination?.limit || 10);
      } else {
        loadUsers(page, state.pagination?.limit || 10);
      }
    },
    
    changePageSize: (limit: number) => {
      const hasActiveSearch = state.searchQuery.trim() || state.searchFilters.role || state.searchFilters.disabled !== undefined;
      if (hasActiveSearch) {
        searchUsers(state.searchQuery, state.searchFilters, 1, limit);
      } else {
        loadUsers(1, limit);
      }
    },
  };
}
